import { useQuery } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { skillKeys } from '~/lib/query-keys';

/**
 * FRONT-2364: the one structured shape a skill's frontmatter+body round
 * trips through — the frontend's own copy of the backend's `SkillDefinition`
 * (`src/skills/skill-definition.ts`). A plain interface, not logic, so it
 * carries no "hand-copied twin" guard obligation the way the now-deleted
 * `skill-frontmatter.ts` did (that file mirrored PARSING RULES, which can
 * silently drift; this only mirrors a wire SHAPE, so a mismatch shows up
 * immediately as a `tsc` error at every call site instead).
 */
export interface SkillDefinition {
  name: string;
  description: string;
  directTriggers: string[];
  suggestTriggers: string;
  tools: string[];
  agents: string[];
  output: string;
  command: string;
  model: string | null;
  instructions: string;
  extra?: Record<string, string | string[]>;
}

export interface Skill {
  slug: string;
  name: string;
  description: string;
  content?: string;
  /** Whether this skill is a pristine built-in, a tenant-customised built-in,
   *  or a fully user-created skill. Always present in the API response. */
  source: 'default' | 'modified' | 'custom';
  /** Set when the skill's stored frontmatter can't be parsed. The row
   *  still exists in the DB but is invisible to the agent catalog;
   *  the UI surfaces it under "Needs attention" so the user can
   *  open the editor and fix the dashes. */
  broken?: boolean;
  brokenReason?: string;
  /** Also on the list payload (the server's `SkillMeta`), read by the
   *  Skills page search. Absent on a broken row. */
  command?: string;
  directTriggers?: string[];
  suggestTriggers?: string;
}

/** `enabled` lets an always-mounted caller (the command palette, FRONT-2436) read
 *  this list only while it is actually searching; the cache key is unchanged. */
export function useSkills(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: skillKeys.list(),
    enabled: options?.enabled,
    queryFn: async () => {
      const data = await api.get<any>('/api/skills');
      return (Array.isArray(data) ? data : data.skills ?? []) as Skill[];
    },
  });
}

export interface SkillContentResponse {
  slug: string;
  content: string;
  /** FRONT-2364: parsed server-side (`parseSkillDefinition`) every time —
   *  never re-derived on the frontend, so the editor and the backend can
   *  never disagree about how a skill's frontmatter reads. */
  definition: SkillDefinition;
}

export function useSkillContent(slug: string) {
  return useQuery({
    queryKey: skillKeys.detail(slug),
    queryFn: () => api.get<SkillContentResponse>(`/api/skills/${encodeURIComponent(slug)}`),
    enabled: !!slug,
  });
}

export interface SkillFileMeta {
  path: string;
  contentType: string | null;
  sizeBytes: number | null;
  isBinary: boolean;
}

/**
 * FRONT-1694 / ADR-0049 (stoicma review on #1210, finding 1): a skill's
 * bundled scripts/references/assets — metadata only (no content), for the
 * file-management panel's listing. `enabled: !!slug` because this is only
 * meaningful for an existing skill; a not-yet-created one has no rows.
 */
export function useSkillFiles(slug: string) {
  return useQuery({
    queryKey: skillKeys.files(slug),
    queryFn: () => api.get<{ files: SkillFileMeta[] }>(`/api/skills/${encodeURIComponent(slug)}/files`),
    enabled: !!slug,
    select: (data) => data.files,
  });
}
